import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ProductCard } from '../components/custom/ProductCard';
import { AgeSelector } from '../components/custom/AgeSelector';
import { ReelShowcaseCarousel } from '../components/custom/ReelShowcase';
import { BundleCarousel } from '../components/custom/BundleCarousel';
import { WelcomeBanner } from '../components/custom/WelcomeBanner';
import { SocialProofNotifications } from '../components/custom/SocialProofNotifications';
import { RecentlyViewed } from '../components/custom/RecentlyViewed';
import { Skeleton } from '../components/ui/skeleton';
import { Carousel, CarouselContent, CarouselItem } from '../components/ui/carousel';
import { Separator } from '../components/ui/separator';
import { 
  Truck, Shield, Heart, ArrowRight, Package, Star, ShoppingBag,
  TrendingUp, Zap, Clock, Gift, Flame, BookOpen, Sparkles, Timer,
  Book, Backpack, Baby, Shirt, Footprints, Trophy, Palette, GraduationCap, ChevronDown
} from 'lucide-react';
import { SEO, SEOPresets } from '../components/SEO';

// Category icon mapping
const categoryIcons = {
  'books': Book,
  'bags': Backpack,
  'baby-care': Baby,
  'clothing': Shirt,
  'footwear': Footprints,
  'sports': Trophy,
  'arts-crafts': Palette,
  'education': GraduationCap,
  'toys': Gift,
  'tiffin': Package,
  'bottles': Package,
  'stationery': BookOpen
};

// Default/fallback banners
const defaultBanners = [
  {
    title: 'Welcome to Mothering',
    subtitle: 'Quality products for your little ones',
    cta_text: 'Shop Now',
    cta_link: '/products',
    badge: 'Free Delivery',
    image_url: 'https://images.pexels.com/photos/3985078/pexels-photo-3985078.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    title: 'New Arrivals',
    subtitle: 'Fresh picks for kids 0-12 years',
    cta_text: 'Explore',
    cta_link: '/products',
    badge: 'New',
    image_url: 'https://images.pexels.com/photos/6995130/pexels-photo-6995130.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    title: 'Best Sellers',
    subtitle: 'Parent-approved favorites',
    cta_text: 'View All',
    cta_link: '/products',
    badge: 'Trending',
    image_url: 'https://images.unsplash.com/photo-1517728848779-e95acb6ac40f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMHRveXMlMjBmbGF0JTIwbGF5JTIwY29sb3JmdWx8ZW58MHx8fGJsdWV8MTc3NDc1OTk0N3ww&ixlib=rb-4.1.0&q=85'
  }
];

// Simple Category Accordion Component
const CategoryAccordion = ({ category, products, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-4 flex items-center gap-3 hover:bg-accent transition-colors"
      >
        <Icon className="w-5 h-5 text-primary shrink-0" />
        <span className="font-semibold flex-1 text-left">{category.name}</span>
        <Badge variant="secondary" className="shrink-0">{products.length}</Badge>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="px-4 pb-4 pt-2">
          <div 
            className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {products.map((product) => (
              <div key={product.id} className="w-[160px] sm:w-[190px] flex-shrink-0 snap-start">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function HomePage() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [reels, setReels] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAge, setSelectedAge] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, productsRes, reelsRes, blogsRes, bannersRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products?limit=40`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reels`).catch(() => ({ json: () => [] })),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blogs?limit=3`).catch(() => ({ json: () => ({ blogs: [] }) })),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/banners/?active_only=true`).catch(() => ({ json: () => [] }))
        ]);

        const categoriesData = await categoriesRes.json();
        const productsData = await productsRes.json();
        const reelsData = await reelsRes.json();
        const blogsData = await blogsRes.json();
        const bannersData = await bannersRes.json();

        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        
        // Don't shuffle here - use daily-based shuffle in the render logic
        setProducts(productsData.products || []);
        
        setReels(Array.isArray(reelsData) ? reelsData : []);
        setBlogs(blogsData?.blogs || []);
        
        // Use admin banners if available, otherwise use defaults
        const activeBanners = Array.isArray(bannersData) && bannersData.length > 0 
          ? bannersData 
          : defaultBanners;
        setBanners(activeBanners);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setBanners(defaultBanners); // Fallback to defaults on error
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Product slices with time-based randomization for consistency
  // Use date as seed so products rotate daily instead of every page load
  const getDailySeed = () => {
    const today = new Date();
    return today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  };
  
  // Seeded shuffle function for consistent daily randomization
  const seededShuffle = (array, seed) => {
    const shuffled = [...array];
    let currentSeed = seed;
    
    const random = () => {
      currentSeed = (currentSeed * 9301 + 49297) % 233280;
      return currentSeed / 233280;
    };
    
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  const dailySeed = getDailySeed();
  const dailyShuffledProducts = seededShuffle(products, dailySeed);
  
  const trendingProducts = dailyShuffledProducts.slice(0, 8);
  const dealProducts = dailyShuffledProducts
    .filter(p => p.original_price && p.original_price > p.price)
    .slice(0, 4);
  const bestSellers = dailyShuffledProducts.slice(8, 16);
  const newArrivals = dailyShuffledProducts.slice(-8);

  // Calculate deal countdown (example: 24 hours from now)
  const [dealTimeLeft, setDealTimeLeft] = useState('23:59:45');

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight - now;
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setDealTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO {...SEOPresets.home} />

      {/* Welcome Banner with WELCOME10 code */}
      <WelcomeBanner />

      {/* Social Proof Notifications */}
      <SocialProofNotifications />

      {/* Hero Carousel */}
      <section className="relative" data-testid="home-hero-carousel">
        {loading ? (
          <Skeleton className="w-full aspect-[1.8/1] sm:aspect-[2.5/1]" />
        ) : (
          <Carousel className="w-full" opts={{ loop: true, align: "start" }}>
            <CarouselContent>
              {banners.map((banner, index) => (
                <CarouselItem key={banner.id || index}>
                  <div className="relative aspect-[1.8/1] sm:aspect-[2.5/1] overflow-hidden">
                    <img 
                      src={banner.image_url} 
                      alt={banner.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
                    <div className="absolute inset-0 flex items-center">
                      <div className="container mx-auto px-4">
                        <div className="max-w-lg space-y-3 sm:space-y-4">
                          {banner.badge && (
                            <Badge className="bg-primary text-primary-foreground">
                              {banner.badge}
                            </Badge>
                          )}
                          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight">
                            {banner.title}
                          </h2>
                          {banner.subtitle && (
                            <p className="text-sm sm:text-base text-white/90">
                              {banner.subtitle}
                            </p>
                          )}
                          <Button 
                            size="lg"
                            className="bg-primary hover:bg-primary/90"
                            onClick={() => navigate(banner.cta_link || '/products')}
                            data-testid="hero-carousel-cta"
                          >
                            {banner.cta_text}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        )}
      </section>

      {/* Category Quick Access */}
      <section className="py-5 sm:py-7" data-testid="category-quick-access-section">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between gap-3 mb-3">
            <h3 className="text-lg sm:text-xl font-heading font-semibold">Shop by Category</h3>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 lg:grid-cols-8">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 lg:grid-cols-8" data-testid="category-quick-access-grid">
              {categories.slice(0, 12).map((cat) => {
                const slug = cat.slug || cat.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
                const IconComponent = categoryIcons[slug] || Package;
                return (
                  <button
                    key={cat.id}
                    onClick={() => navigate(`/products?category=${cat.id}`)}
                    className="group bg-card border rounded-xl p-3 hover:border-primary hover:shadow-md transition-all duration-200 active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid={`home-category-tile-${slug}`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors overflow-hidden">
                        {cat.image_url ? (
                          <img 
                            src={cat.image_url} 
                            alt={cat.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                        )}
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-center line-clamp-2">
                        {cat.name}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Age-Based Shopping */}
      <section className="py-5 sm:py-7 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between gap-3 mb-3">
            <h3 className="text-lg sm:text-xl font-heading font-semibold">Shop by Age</h3>
          </div>
          <AgeSelector selectedAge={selectedAge} onAgeSelect={setSelectedAge} />
        </div>
      </section>

      {/* Bundles Section */}
      <BundleCarousel />

      {/* Trending Products Rail */}
      <section className="py-5 sm:py-7" data-testid="trending-products-section">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h3 className="text-lg sm:text-xl font-heading font-semibold">Trending Now</h3>
            </div>
            <Link to="/products" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="see-all-link">
              See all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="w-[160px] sm:w-[190px] h-[240px] flex-shrink-0 rounded-xl" />
              ))}
            </div>
          ) : (
            <div 
              className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide" 
              data-testid="featured-products-rail"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {trendingProducts.map((product) => (
                <div key={product.id} className="w-[160px] sm:w-[190px] flex-shrink-0 snap-start">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Deal of the Day */}
      {dealProducts.length > 0 && (
        <section className="py-5 sm:py-7 bg-gradient-to-br from-accent/40 to-accent/20" data-testid="deal-of-day-section">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Flame className="w-6 h-6 text-orange-500" />
                <h3 className="text-lg sm:text-xl font-heading font-bold">Deal of the Day</h3>
              </div>
              <Badge className="font-mono text-xs bg-yellow-400 text-yellow-950 px-3 py-1" data-testid="deal-timer">
                <Timer className="w-3 h-3 mr-1" />
                {dealTimeLeft}
              </Badge>
            </div>

            <div 
              className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {dealProducts.map((product) => (
                <div key={product.id} className="w-[160px] sm:w-[190px] flex-shrink-0 snap-start">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Best Sellers Grid */}
      <section className="py-5 sm:py-7" data-testid="best-sellers-section">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg sm:text-xl font-heading font-semibold">Best Sellers</h3>
            </div>
            <Link to="/products" className="text-sm text-primary hover:underline flex items-center gap-1">
              See all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-[280px] rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="best-sellers-grid">
              {bestSellers.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Shop by Category (Expandable) */}
      <section className="py-5 sm:py-7 bg-muted/30" data-testid="shop-by-category-section">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between gap-3 mb-3">
            <h3 className="text-lg sm:text-xl font-heading font-semibold">Explore Categories</h3>
          </div>

          <div className="space-y-2">
            {categories.slice(0, 6).map((cat) => {
              const slug = cat.slug || cat.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
              const categoryProducts = products.filter(p => 
                p.category_name === cat.name
              ).slice(0, 6);
              if (categoryProducts.length === 0) return null;

              const IconComponent = categoryIcons[slug] || Package;

              return (
                <CategoryAccordion
                  key={cat.id}
                  category={cat}
                  products={categoryProducts}
                  icon={IconComponent}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      {newArrivals.length > 0 && (
        <section className="py-5 sm:py-7" data-testid="new-arrivals-section">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <h3 className="text-lg sm:text-xl font-heading font-semibold">New Arrivals</h3>
              </div>
              <Link to="/products" className="text-sm text-primary hover:underline flex items-center gap-1">
                See all
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div 
              className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {newArrivals.map((product) => (
                <div key={product.id} className="w-[160px] sm:w-[190px] flex-shrink-0 snap-start">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recently Viewed */}
      <RecentlyViewed />

      {/* Blog Content */}
      {blogs.length > 0 && (
        <section className="py-5 sm:py-7 bg-muted/30" data-testid="blog-section">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg sm:text-xl font-heading font-semibold">Parenting Tips</h3>
              </div>
              <Link to="/blog" className="text-sm text-primary hover:underline flex items-center gap-1">
                View all
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {blogs.map((blog) => (
                <Link 
                  key={blog.id} 
                  to={`/blog/${blog.id}`}
                  className="block"
                >
                  <Card 
                    className="hover:shadow-lg transition-shadow cursor-pointer overflow-hidden h-full" 
                    data-testid="blog-card"
                  >
                    {blog.featured_image && (
                      <img 
                        src={blog.featured_image} 
                        alt={blog.title}
                        className="w-full h-40 object-cover"
                      />
                    )}
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2 line-clamp-2 text-base">{blog.title}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {blog.excerpt || (blog.content ? blog.content.replace(/<[^>]*>/g, '').substring(0, 120) : 'Read more...')}
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        {blog.created_at && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(blog.created_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reels Showcase */}
      {reels.length > 0 && (
        <section className="py-5 sm:py-7" data-testid="reels-section">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between gap-3 mb-3">
              <h3 className="text-lg sm:text-xl font-heading font-semibold">Product Videos</h3>
            </div>
            <ReelShowcaseCarousel reels={reels} />
          </div>
        </section>
      )}

      {/* Trust Badges */}
      <section className="py-5 sm:py-7" data-testid="trust-badges-section">
        <div className="container mx-auto px-4">
          <div 
            className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" 
            data-testid="trust-badges-row"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <Badge variant="secondary" className="flex-shrink-0 gap-2 px-4 py-2 text-sm" data-testid="trust-badge">
              <Truck className="w-4 h-4" />
              Free Delivery
            </Badge>
            <Badge variant="secondary" className="flex-shrink-0 gap-2 px-4 py-2 text-sm" data-testid="trust-badge">
              <Shield className="w-4 h-4" />
              100% Secure
            </Badge>
            <Badge variant="secondary" className="flex-shrink-0 gap-2 px-4 py-2 text-sm" data-testid="trust-badge">
              <Heart className="w-4 h-4" />
              Quality Assured
            </Badge>
            <Badge variant="secondary" className="flex-shrink-0 gap-2 px-4 py-2 text-sm" data-testid="trust-badge">
              <Package className="w-4 h-4" />
              Easy Returns
            </Badge>
            <Badge variant="secondary" className="flex-shrink-0 gap-2 px-4 py-2 text-sm" data-testid="trust-badge">
              <Zap className="w-4 h-4" />
              Fast Support
            </Badge>
          </div>
        </div>
      </section>

      {/* Why Shop With Us */}
      <section className="py-8 sm:py-12 bg-gradient-to-br from-primary/5 to-transparent" data-testid="why-shop-section">
        <div className="container mx-auto px-4">
          <h3 className="text-xl sm:text-2xl font-heading font-bold text-center mb-8">Why Shop with Mothering?</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <Card className="text-center" data-testid="why-shop-card">
              <CardContent className="pt-6 pb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Quality Guaranteed</h4>
                <p className="text-sm text-muted-foreground">
                  Every product is carefully selected for safety and durability
                </p>
              </CardContent>
            </Card>

            <Card className="text-center" data-testid="why-shop-card">
              <CardContent className="pt-6 pb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Free Delivery</h4>
                <p className="text-sm text-muted-foreground">
                  Complimentary shipping on all orders, no minimum purchase
                </p>
              </CardContent>
            </Card>

            <Card className="text-center" data-testid="why-shop-card">
              <CardContent className="pt-6 pb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Parent Approved</h4>
                <p className="text-sm text-muted-foreground">
                  Trusted by thousands of parents across India
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;